// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useEffect } from 'react';

import { Accordion, Text, Box } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { renderTime, WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomIconTypes, IconTypes } from 'components/Icons';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { ResourceActionsPatternAccordion } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/ResourceActionsPatternAccordion';
import { renderActionPerformed } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { CloudAccountName } from 'containers/Setup/CloudAccounts/utils';
import { formatDate } from 'utils/date';

export const S3Activity: FC = () => {
  const dispatch = useDispatch();

  const {
    resourceDetails,
    resourceAccountId,
    resourceInsight,
    activity,
    permissions,
  } = useSelector(selectActiveResource);
  const { s3InternetActivity, s3CrossAccount } = activity;
  const { getPermissions } = useActiveResourceActions();

  useEffect(() => {
    getPermissions();
  }, [getPermissions]);

  useEffect(() => {
    const { data: detail } = resourceDetails;
    const { data: insight } = resourceInsight;
    dispatch(
      actions.getS3CrossAccount({
        q: {
          accountId: resourceAccountId,
          bucketArn: insight?.arn ?? insight?.identifier?.CertificateArn ?? '',
          region: detail?.general?.region ?? '',
        },
      }),
    );

    dispatch(
      actions.getS3InternetActivity({
        q: {
          accountId: resourceAccountId,
          bucketArn: insight?.arn ?? insight?.identifier?.CertificateArn ?? '',
          region: detail?.general?.region ?? '',
        },
      }),
    );
  }, [dispatch, resourceAccountId, resourceDetails, resourceInsight]);

  const None = <Text color={customTheme.colors.gray['400']}>None</Text>;

  const suspiciousInternetAccessColumns: TableColumnProps[] = [
    {
      header: 'COUNTRY',
      accessor: 'country',
      render: ({ value }) => (
        <WithResourceIcon
          resourceType={IconTypes.ExclamationTriangle}
          color={'critical'}
        >
          <Box>{value}</Box>
        </WithResourceIcon>
      ),
    },
    {
      header: 'TIMESTAMP',
      accessor: 'timestamp',
      render: ({ value }) => formatDate(value),
      align: 'left',
    },
    {
      header: 'ACTION PERFORMED',
      accessor: '',
      render: () => '~',
      align: 'left',
    },
    {
      header: 'SERVICE',
      accessor: 'service',
      render: ({ value }) => value || None,
      align: 'left',
    },
    {
      header: 'FROM PROVIDER',
      accessor: 'provider',
      render: ({ value }) => value || None,
      align: 'left',
    },
    {
      header: 'REASON',
      accessor: '~',
    },
  ];
  const crossAccountColumns: TableColumnProps[] = [
    {
      header: 'ACCOUNT ID',
      accessor: 'account_id',
      render: ({ value }) => (
        <WithResourceIcon resourceType={IconTypes.Account}>
          {value !== 'anonymous' ? (
            <CloudAccountName accountId={value} />
          ) : (
            <Text>{value}</Text>
          )}
        </WithResourceIcon>
      ),
      align: 'left',
    },
    {
      header: 'TIMESTAMP',
      accessor: 'timestamp',
      render: ({ value }) => formatDate(value),
      align: 'left',
    },
    {
      header: 'ACTION PERFORMED',
      align: 'left',
      render: () => '~',
    },
    {
      header: 'SRC REGION',
      accessor: 'region',
      align: 'left',
    },
    {
      header: 'REASON',
      render: () => '~',
      align: 'left',
    },
  ];

  const insecureACLUsageColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Account ID</Box>,
      accessor: 'account_id',
      render: ({ value }) => (
        <WithResourceIcon resourceType={IconTypes.Account}>
          <Box>{value}</Box>
        </WithResourceIcon>
      ),
    },
    {
      header: 'TIMESTAMP',
      accessor: 'timestamp',
      align: 'left',
      render: renderTime,
    },
    {
      header: 'Action Performed',
      accessor: 'action',
      align: 'left',
      render: renderActionPerformed,
    },
    {
      header: 'Region',
      accessor: 'region',
      align: 'left',
    },
    {
      header: 'ACL Used',
      accessor: 'acl_used',
      align: 'left',
    },
  ];

  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <ResourceActionsPatternAccordion />
      <MetaAccordionItem
        iconType={IconTypes.Hacker}
        label={'Suspicious internet access during the past 90 days'}
        onClickDownload={() => {}}
        panel={
          <Table
            columns={suspiciousInternetAccessColumns}
            data={s3InternetActivity.data ?? []}
            isLoading={s3InternetActivity.isLoading}
          />
        }
        isLoading={s3InternetActivity.isLoading}
        totalCount={s3InternetActivity.data?.length ?? 0}
        iconBGColor="critical"
      />

      <MetaAccordionItem
        iconType={CustomIconTypes.Egress}
        label={'Cross-account access during past 90 days'}
        onClickDownload={() => {}}
        panel={
          <Table
            columns={crossAccountColumns}
            data={s3CrossAccount.data ?? []}
            isLoading={s3CrossAccount.isLoading}
          />
        }
        totalCount={s3CrossAccount.data?.length ?? 0}
        isLoading={s3CrossAccount.isLoading}
        iconBGColor="critical"
      />
      <MetaAccordionItem
        iconType={CustomIconTypes.Permission}
        label="Insecure ACL usage during the past 90 days"
        totalCount={permissions.insecureAcl.data?.length ?? 0}
        isLoading={permissions.insecureAcl.isLoading}
        onClickDownload={() => {}}
        panel={
          <Table
            columns={insecureACLUsageColumns}
            data={permissions.insecureAcl.data}
            isLoading={permissions.insecureAcl.isLoading}
          />
        }
        iconBGColor="critical"
      />
    </Accordion>
  );
};
